import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { TrashIcon } from '@heroicons/react/solid'

import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import Card from '@/shared/Card'
import Loading from '@/shared/Loading'
import PrimaryButton from '@/shared/Buttons/Primary'

const statusBadge = {
  pending:  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  accepted: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  revoked:  'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
}

const ProjectTeamIndex = () => {
  const { id: projectId } = useParams()
  const [, setToast] = useGlobalState('toast')
  const { getRequest, putpostRequest } = useQuery()
  const [collaborators, setCollaborators] = useState([])
  const [canManage, setCanManage] = useState(false)
  const [loading, setLoading] = useState(true)
  const [username, setUsername] = useState('')
  const [inviting, setInviting] = useState(false)
  const [error, setError] = useState(null)
  const [confirmingRevoke, setConfirmingRevoke] = useState(null)

  useEffect(() => { fetchData() }, [])

  const fetchData = () => {
    setLoading(true)
    getRequest(`/api/v3/manage/projects/${projectId}/project_collaborators`, {}, (err, jsonData) => {
      setLoading(false)
      if (err) { return }
      setCollaborators(jsonData.collaborators || [])
      setCanManage(!!jsonData.canManage)
    })
  }

  const invite = (e) => {
    e.preventDefault()
    if (!username.trim()) { return }
    setInviting(true)
    setError(null)
    putpostRequest(`/api/v3/manage/projects/${projectId}/project_collaborators`, 'POST', { username: username.trim() }, (err, jsonData) => {
      setInviting(false)
      if (err) {
        setError(typeof err === 'string' ? err : (err?.errors || 'Could not send invite'))
        return
      }
      setCollaborators((prev) => {
        const without = prev.filter((c) => c.user.id !== jsonData.collaborator.user.id)
        return [...without, jsonData.collaborator]
      })
      setUsername('')
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-white text-gray-800">Invite sent to {jsonData.collaborator.user.displayName}</p>
      </div>)
    })
  }

  const revoke = (collaborator) => {
    if (confirmingRevoke !== collaborator.id) {
      setConfirmingRevoke(collaborator.id)
      return
    }
    putpostRequest(`/api/v3/manage/projects/${projectId}/project_collaborators/${collaborator.id}`, 'DELETE', {}, (err) => {
      setConfirmingRevoke(null)
      if (err) { return }
      setCollaborators((prev) => prev.filter((c) => c.id !== collaborator.id))
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-white text-gray-800">Collaborator removed</p>
      </div>)
    })
  }

  return <Card title='Team'>
    {canManage ? (
      <div className='p-3 rounded-md bg-gray-50 dark:bg-gray-800 mb-4'>
        <p className='text-sm text-gray-700 dark:text-gray-200 mb-2'>
          Invite another CCC member to help manage this project. They&apos;ll be able to edit the project, manage roles, review submissions, and see the project DMs — just like you can.
        </p>
        <form onSubmit={invite} className='flex flex-col sm:flex-row gap-2'>
          <input
            type='text'
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder='Username'
            className='flex-1 shadow-sm dark:bg-gray-900 dark:text-white focus:ring-cccpurple focus:border-cccpurple block w-full sm:text-sm border-gray-300 rounded-md'
          />
          <PrimaryButton type='submit' onClick={invite} loading={inviting} text='Send invite' />
        </form>
        {error && <p className='mt-2 text-sm text-red-600'>{Array.isArray(error) ? error.join(', ') : error}</p>}
      </div>
    ) : (
      <p className='text-sm text-gray-500 dark:text-gray-400 mb-4'>
        Read-only view. Only the project owner can invite or remove collaborators.
      </p>
    )}

    {loading && <Loading />}

    {!loading && collaborators.length === 0 && (
      <p className='text-sm text-gray-500 dark:text-gray-400'>
        {canManage ? 'No collaborators yet. Invite someone above to get started.' : 'No collaborators on this project yet.'}
      </p>
    )}

    {!loading && collaborators.length > 0 && (
      <ul className='space-y-2'>
        {collaborators.map((c) => (
          <li key={`collab${c.id}`} className='flex items-center justify-between gap-2 p-2 bg-white dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600'>
            <div className='flex items-center gap-3 min-w-0'>
              <img className='h-8 w-8 rounded-sm flex-shrink-0' src={c.user.imageUrl} alt='' />
              <div className='min-w-0'>
                <div className='flex items-center gap-2 flex-wrap'>
                  <a href={`/m/${c.user.username}`} className='text-sm font-medium text-cccblue hover:underline truncate'>{c.user.displayName}</a>
                  <span className='text-xs text-gray-500'>@{c.user.username}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadge[c.status] || statusBadge.pending}`}>{c.status}</span>
                </div>
                <div className='text-xs text-gray-500'>Invited {c.invitedAt}{c.respondedAt ? ` · Responded ${c.respondedAt}` : ''}</div>
              </div>
            </div>
            {canManage && (
              <button
                type='button'
                onClick={() => revoke(c)}
                className={`text-xs ${confirmingRevoke === c.id ? 'text-red-600 font-semibold' : 'text-gray-400 hover:text-red-500'}`}
                title='Revoke / remove'
              >
                {confirmingRevoke === c.id ? 'Really remove?' : <TrashIcon className='h-4 w-4' />}
              </button>
            )}
          </li>
        ))}
      </ul>
    )}
  </Card>
}

export default ProjectTeamIndex
